import {
  AfterViewInit,
  DestroyRef,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  Renderer2,
  inject,
} from '@angular/core';
import { SearchResultsPaginationComponent } from '../../components';
import { SearchInstancesManagerService } from '../../services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, switchMap } from 'rxjs/operators';
import { tap } from 'rxjs';
import { OverlapElementTree } from '../../models';
import { delayedPromise } from '@anecdotes/utils';

@Directive({
  selector: '[searchableRowByIds]',
})
export class SearchableRowByIdsDirective implements AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private isCurrent: boolean;
  private currentHighlightedElements: Element[];

  private get _rowTrackBy(): (item) => any {
    return this.rowTrackBy || ((item) => item); // if rowTrackBy is null returns default rowTrackBy
  }

  @Input()
  searchableRowByIds: any;

  @Input()
  scrollToElementOnFocus = false;

  @Input()
  rowTrackBy: (item) => any;

  @Output()
  searchFocusIn = new EventEmitter();

  @Output()
  searchFocusLeave = new EventEmitter();

  @Output()
  currentOverlapChange = new EventEmitter<OverlapElementTree | null>();

  @Output()
  elementNotFoundInDOM = new EventEmitter<string>();

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private searchInstancesManagerService: SearchInstancesManagerService,
    private renderer: Renderer2,
  ) {
    this.elementRef.nativeElement.classList.add('x-searchable-row');
  }

  ngAfterViewInit(): void {
    this.searchInstancesManagerService
      .getSearchResultsPaginator(this.searchInstancesManagerService.getSearchScopeKey(this.elementRef.nativeElement))
      .pipe(
        filter(Boolean),
        tap((searchPaginator) => this.handleDataFocusChange(searchPaginator)),
        switchMap((searchPaginator) => {
          return searchPaginator.dataFocusChange.pipe(tap(() => this.handleDataFocusChange(searchPaginator)));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private handleDataFocusChange(searchPaginator: SearchResultsPaginationComponent): void {
    const isCurrent = this.isCurrentElement(searchPaginator);

    if (this.isCurrent && !isCurrent) {
      this.searchFocusLeave.emit();
      this.currentOverlapChange.emit(null);
    }

    this.isCurrent = isCurrent;

    if (this.isCurrent && searchPaginator.currentOverlapIndexEntry.overlapElementTree) {
      this.handleSearchableRowElementFind(searchPaginator);
    }
  }

  private isCurrentElement(searchPaginator: SearchResultsPaginationComponent): boolean {
    if (searchPaginator.currentOverlapIndexEntry) {
      return (
        this._rowTrackBy(searchPaginator.currentOverlapIndexEntry.row) === this._rowTrackBy(this.searchableRowByIds)
      );
    }
  }

  private async handleSearchableRowElementFind(searchPaginator: SearchResultsPaginationComponent): Promise<void> {
    this.searchFocusIn.emit();
    this.currentOverlapChange.emit(searchPaginator.currentOverlapIndexEntry.overlapElementTree);

    this.unmarkCurrentHighlight();
    this.dispatchLeave();
    this.findOverlapElement(searchPaginator.currentOverlapIndexEntry.overlapElementTree);
  }

  private unmarkCurrentHighlight(): void {
    document.body
      .querySelectorAll('.search-highlight.current-highlight')
      .forEach((element: Element) => this.renderer.removeClass(element, 'current-highlight'));
  }

  private markCurrentHighlight(): void {
    if (this.currentHighlightedElements?.length) {
      this.currentHighlightedElements.forEach((element: Element) =>
        this.renderer.addClass(element, 'current-highlight'),
      );
    }
  }

  private async findOverlapElement(overlapElementTree: OverlapElementTree): Promise<void> {
    const selectors = this.createSelectorsFromIds(overlapElementTree);

    this.currentHighlightedElements = await this.queryCurrentHighlightElementWrapper(selectors)
      // we use delayedPromise because the queryCurrentHighlightElementWrapper method
      // finds elements faster than the SearchableTextDirective.setHighlightIds method assigns search-highlight index
      .then((element) => delayedPromise(200).then(() => element))
      .then(
        (element: Element | null): Element[] => {
          if (element) {
            const overlapsInOneElement = element.querySelectorAll('.search-highlight');
            const matchIndex = this.getMatchIndex(overlapElementTree);

            const filteredByHighlightIndex = Array.from(overlapsInOneElement).filter((element) => {
              const index = element.getAttribute('highlight-index');

              return index && Number(index) === matchIndex;
            });

            return filteredByHighlightIndex;
          } else {
            return [];
          }
        },
      );

    this.markCurrentHighlight();
    this.dispatchFocus();
  }

  private dispatchFocus(): void {
    if (this.currentHighlightedElements?.length) {
      const wrapperOfCurrentHighlightedEl: Element | null =
        this.currentHighlightedElements[0].closest('.searchable-text');

      wrapperOfCurrentHighlightedEl?.dispatchEvent(new Event('focusTerm'));

      if (this.scrollToElementOnFocus) {
        setTimeout(() => {
          this.currentHighlightedElements[0].scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'start' });
        }, 200);
      }
    }
  }

  private dispatchLeave(): void {
    if (this.currentHighlightedElements?.length) {
      const wrapperOfCurrentHighlightedEl: Element | null =
        this.currentHighlightedElements[0].closest('.searchable-text');

      wrapperOfCurrentHighlightedEl?.dispatchEvent(new Event('focusTermRemoved'));
    }
  }

  private async queryCurrentHighlightElementWrapper(selectors: string[]): Promise<Element | null> {
    let element: Element | null;

    for (const selector of selectors) {
      const nestedElement: Element | null = await this.waitForElementRender(selector, element);

      if (!nestedElement) {
        break;
      } else {
        element = nestedElement;
      }
    }

    return element;
  }

  private createSelectorsFromIds(elementTree: OverlapElementTree): string[] {
    const selector = [`[search-id="${elementTree.searchId}"]`];

    if (elementTree.propertyId) {
      selector.push(`[search-property-id="${elementTree.propertyId}"]`);
    }

    if (elementTree.nestedElement) {
      selector.push(...this.createSelectorsFromIds(elementTree.nestedElement));
    }

    return selector;
  }

  private getMatchIndex(tree: OverlapElementTree): number {
    return tree.matchIndex ?? this.getMatchIndex(tree.nestedElement!);
  }

  private waitForElementRender(selector: string, rootElement = document.body as Element): Promise<Element | null> {
    return new Promise<Element | null>((resolve) => {
      const element = rootElement.querySelector(selector);

      if (element) {
        return resolve(element);
      }

      const timeout = setTimeout(() => {
        observer.disconnect();
        this.elementNotFoundInDOM.emit(selector);
        resolve(null);
      }, 5000);

      const observer = new MutationObserver(() => {
        const element = rootElement.querySelector(selector);

        if (element) {
          observer.disconnect();
          clearTimeout(timeout);
          resolve(element);
        }
      });

      observer.observe(rootElement, {
        childList: true,
        subtree: true,
      });
    });
  }
}
